/**
 * @file heldHere.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description Approved here, not yet in Xero (Cam, 3 Nov 2026 — month end:
 *              "Need to be able to set invoices as approved/ authorised
 *              local-only ready to send on statements even when stock issues
 *              or whatever block this going to Xero. When this is done there
 *              needs to be a persistant alert raised to rectify it later.").
 *              The screens' half: what the server's answers mean, the words,
 *              and the two calls. The rules are the server's
 *              (services/invoiceHeldHereService, utils/invoiceHeldHere).
 *
 *   approveHereOfferOf(status, body)  the 409 XERO_REFUSED_APPROVE_HERE offer, or null
 *   refusedQuestion(entries)          the one question for one or many refused invoices
 *   holdHere(invoiceId)               POST /api/invoices/:id/hold-here ("Keep held here")
 *   sendHeldNow(invoiceId)            POST /api/invoices/:id/send-held ("Send to Xero now")
 */

import { API_URL } from '../config';

/** The offer a refused draft approval answers with, or null. */
export function approveHereOfferOf(status, body) {
  if (status !== 409 || body?.code !== 'XERO_REFUSED_APPROVE_HERE') return null;
  return body?.details?.approve_here || null;
}

/**
 * The question for invoices Xero refused (a draft's approval, or an approved
 * invoice that did not go). Pure.
 * @param {Array<{ invoice_no, reason }>} entries
 * @returns {{ title, message, confirmText }}
 */
export function refusedQuestion(entries) {
  const list = (entries || []).filter(Boolean);
  const one = list.length === 1 ? list[0] : null;
  const after = 'It goes on statements now, is sent to Xero by itself once Xero takes it (put the stock right — enter the '
    + 'bill — or whatever Xero says), and an alert stays for everyone until it is in Xero.';
  if (one) {
    return {
      title: `Xero refused ${one.invoice_no || 'the invoice'}`,
      message: `Xero refused ${one.invoice_no || 'it'}: ${one.reason || 'no reason given'}.\n\nApprove it here only? ${after}`,
      confirmText: 'Approve here only'
    };
  }
  const lines = list.map(e => `• ${e.invoice_no || 'An invoice'}: ${e.reason || 'no reason given'}`).join('\n');
  return {
    title: `Xero refused ${list.length} invoices`,
    message: `Xero refused these:\n${lines}\n\nApprove them here only? ${after.replace('It goes', 'Each goes').replace('it is in Xero', 'each is in Xero')}`,
    confirmText: 'Approve here only'
  };
}

async function post(path) {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}'
  });
  let body = null;
  try { body = await res.json(); } catch (_) { body = null; }
  if (!res.ok || body?.success === false) {
    const err = new Error(body?.error || `Could not do that (HTTP ${res.status})`);
    err.code = body?.code || null;
    throw err;
  }
  return body || {};
}

/** "Keep held here": an approved invoice Xero refused on its way. */
export const holdHere = (invoiceId) => post(`/api/invoices/${encodeURIComponent(invoiceId)}/hold-here`);

/** "Send to Xero now" for a held invoice. Answers { outcome, message }. */
export const sendHeldNow = (invoiceId) => post(`/api/invoices/${encodeURIComponent(invoiceId)}/send-held`);
